import React, { useEffect, useState } from 'react'
import { apiClient } from '@/utils/api'
import { Gift, Copy, Check, Share2, Loader2 } from 'lucide-react'

interface ReferralInfo {
  code: string
  share_url: string
  referrals_count: number
  months_earned: number
  cap: number
  remaining: number
}

export const ReferralCard: React.FC = () => {
  const [info, setInfo] = useState<ReferralInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let active = true
    apiClient
      .getReferralInfo()
      .then((data) => {
        if (active) setInfo(data)
      })
      .catch(() => {
        if (active) setError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const copyLink = async () => {
    if (!info) return
    try {
      await navigator.clipboard.writeText(info.share_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard may be unavailable; selection fallback is the input itself.
    }
  }

  const share = async () => {
    if (!info) return
    const shareData = {
      title: 'HousePoints',
      text: 'I use HousePoints to manage our family chores. Use my link and we both get a free month:',
      url: info.share_url,
    }
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData)
        return
      } catch {
        // User cancelled or share failed; fall back to copy.
      }
    }
    copyLink()
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center gap-3 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading your referral link…</span>
      </div>
    )
  }

  // Don't show a broken card if the endpoint isn't available yet.
  if (error || !info) return null

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-lg p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-purple-100">
          <Gift className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Refer a family, both get a free month</h3>
          <p className="text-gray-600 text-sm mt-1">
            Share your link. When a family you invite starts their trial, you each get an extra 30 days free.
          </p>
        </div>
      </div>

      <label className="block text-sm font-medium text-gray-700 mb-2">Your referral link</label>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={info.share_url}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <button
          onClick={copyLink}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          aria-label="Copy referral link"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          onClick={share}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition"
        >
          <Share2 className="h-4 w-4" />
          Share
        </button>
      </div>

      <div className="flex items-center gap-6 mt-4 text-sm">
        <div>
          <span className="font-semibold text-gray-900">{info.referrals_count}</span>
          <span className="text-gray-600"> {info.referrals_count === 1 ? 'family' : 'families'} referred</span>
        </div>
        <div>
          <span className="font-semibold text-gray-900">{info.months_earned}</span>
          <span className="text-gray-600"> free {info.months_earned === 1 ? 'month' : 'months'} earned</span>
        </div>
      </div>
    </div>
  )
}
